'use client'

import React, { useEffect, useState } from 'react'
import { Heart, Trash } from 'lucide-react'
import { useWishlist } from '@/providers/WishlistProvider'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Link } from '@/i18n/routing'
import Image from 'next/image'
import { Price } from '@/components/Price'
import { Button } from '@/components/ui/button'
import { Product } from '@/payload-types'
import { useTranslations } from 'next-intl'
import { useAuth } from '@/providers/Auth'
import { toast } from 'sonner'
import { useRouter } from 'next/navigation'

interface WishlistBadgeClientProps {
  locale: string
  initialCount: number
}

export function WishlistBadgeClient({ locale, initialCount }: WishlistBadgeClientProps) {
  const { wishlist, count, toggle, isPending } = useWishlist()
  const displayCount = count ?? initialCount
  const [isOpen, setIsOpen] = useState(false)
  const [fetchedProducts, setFetchedProducts] = useState<Record<string | number, Product>>({})
  const tProduct = useTranslations('product')
  const { user } = useAuth()
  const router = useRouter()

  useEffect(() => {
    if (!wishlist || wishlist.length === 0) return

    const fetchMissingData = async () => {
      const missingIds = wishlist.filter(id => !fetchedProducts[id])
      if (missingIds.length === 0) return

      try {
        const query = new URLSearchParams()
        query.set('limit', String(missingIds.length))
        query.set('depth', '2')
        missingIds.forEach((id) => query.append('where[id][in]', String(id)))

        const res = await fetch(`/api/products?${query.toString()}`)
        if (res.ok) {
          const json = await res.json()
          const newProducts: Record<string | number, Product> = {}
          json.docs.forEach((p: Product) => {
            newProducts[p.id] = p
          })
          setFetchedProducts((prev) => ({ ...prev, ...newProducts }))
        }
      } catch (e) {
        console.error('Failed to fetch wishlist products', e)
      }
    }

    fetchMissingData()
  }, [wishlist, fetchedProducts])

  const handleOpenChange = (open: boolean) => {
    if (open && !user) {
      toast('Please log in to use the wishlist', {
        action: { label: 'Log in', onClick: () => router.push(`/${locale}/login`) },
      })
      router.push(`/${locale}/login`)
      return
    }
    setIsOpen(open)
  }

  return (
    <Sheet onOpenChange={handleOpenChange} open={isOpen}>
      <SheetTrigger asChild>
        <button
          aria-label={`Wishlist (${displayCount} items)`}
          className="relative flex items-center justify-center p-1.5 sm:p-2.5 hover:bg-white/10 rounded-full transition-all hover:scale-110 duration-200 group cursor-pointer"
        >
          <Heart className="h-4 w-4 sm:h-5 sm:w-5 text-white group-hover:text-[var(--brand-red)]" />
          {displayCount > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-4 w-4 sm:h-4 sm:w-4 items-center justify-center
                         rounded-full bg-rose-500 text-[10px] font-bold text-white leading-none"
              aria-hidden="true"
            >
              {displayCount > 99 ? '99+' : displayCount}
            </span>
          )}
        </button>
      </SheetTrigger>

      <SheetContent className="flex flex-col w-full sm:max-w-md border-l border-neutral-200 dark:border-neutral-800">
        <SheetHeader className="px-6 py-6 border-b border-neutral-100 dark:border-neutral-900">
          <SheetTitle className="font-mono text-xl uppercase tracking-widest flex items-center gap-2">
            <Heart className="w-5 h-5" />
            Wishlist
          </SheetTitle>
          <SheetDescription className="font-mono text-xs text-neutral-800 dark:text-neutral-200 uppercase tracking-wider">
            Your saved items
          </SheetDescription>
        </SheetHeader>

        {!wishlist || wishlist.length === 0 ? (
          <div className="grow flex flex-col items-center justify-center gap-6 p-8">
            <div className="relative flex h-32 w-32 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
              <Heart className="h-12 w-12 text-neutral-400" strokeWidth={1} />
            </div>
            <div className="text-center space-y-4">
              <p className="text-center text-sm font-mono uppercase tracking-widest text-neutral-500 dark:text-neutral-400">
                Your wishlist is empty
              </p>
              <Button
                variant="outline"
                size="sm"
                className="font-mono text-xs uppercase tracking-widest"
                onClick={() => setIsOpen(false)}
                asChild
              >
                <Link href="/shop">Browse Products</Link>
              </Button>
            </div>
          </div>
        ) : (
          <div className="grow flex flex-col overflow-hidden">
            <ul className="grow overflow-y-auto px-6 py-2 custom-scrollbar">
              {wishlist.map((productId) => {
                const product = fetchedProducts[productId]
                
                // Show loading skeleton for items being fetched
                if (!product) {
                  return (
                    <li key={productId} className="flex items-center gap-4 py-6 border-b border-neutral-100 dark:border-neutral-900 last:border-0 animate-pulse">
                      <div className="h-24 w-24 bg-neutral-200 dark:bg-neutral-800 rounded-sm"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-3/4"></div>
                        <div className="h-3 bg-neutral-200 dark:bg-neutral-800 rounded w-1/2"></div>
                      </div>
                    </li>
                  )
                }

                const metaImage =
                  product.meta?.image && typeof product.meta?.image === 'object'
                    ? product.meta.image
                    : undefined

                const firstGalleryImage =
                  product.gallery &&
                    Array.isArray(product.gallery) &&
                    product.gallery.length > 0 &&
                    typeof product.gallery[0]?.image === 'object'
                    ? product.gallery[0].image
                    : undefined

                const image = metaImage || firstGalleryImage
                const price = product.basePrice || (product as any).price

                return (
                  <li
                    className="group relative flex items-center gap-4 py-6 border-b border-neutral-100 dark:border-neutral-900 last:border-0"
                    key={productId}
                  >
                    <div className="absolute top-4 right-0 z-40 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => toggle(String(productId))}
                        disabled={isPending}
                        className="p-2 rounded-full bg-rose-50 hover:bg-rose-100 text-rose-500 transition-colors"
                        aria-label="Remove from wishlist"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex w-full flex-row gap-4">
                      <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-sm border border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950">
                        <Link
                          className="relative block h-full w-full"
                          href={`/products/${product.slug}`}
                          onClick={() => setIsOpen(false)}
                        >
                          {image?.url ? (
                            <Image
                              alt={image?.alt || product?.title || ''}
                              className="object-cover transition-transform duration-500 group-hover:scale-110"
                              fill
                              src={
                                image.url.startsWith('http')
                                  ? image.url
                                  : `${process.env.NEXT_PUBLIC_SERVER_URL || 'http://localhost:3000'}${image.url.startsWith('/') ? '' : '/'}${image.url}`
                              }
                              sizes="96px"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-3xl">📦</div>
                          )}
                        </Link>
                      </div>

                      <div className="flex flex-1 flex-col justify-between py-1 pr-8">
                        <div>
                          <Link
                            href={`/products/${product.slug}`}
                            className="font-mono text-sm uppercase tracking-tight hover:text-rose-500 transition-colors line-clamp-2"
                            onClick={() => setIsOpen(false)}
                          >
                            {product?.title}
                          </Link>
                        </div>

                        <div className="flex items-end justify-between mt-2">
                          {(typeof price === 'number' && (
                            <div className="flex flex-col">
                              <Price
                                amount={price}
                                className="font-mono text-sm font-bold text-emerald-600 dark:text-emerald-400"
                              />
                            </div>
                          )) || (
                              <div className="text-[10px] font-mono uppercase text-muted-foreground">
                                Price N/A
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
            <div className="p-6 bg-neutral-50 dark:bg-neutral-950 border-t border-neutral-100 dark:border-neutral-900">
               <Link
                className="block w-full"
                href="/shop"
                onClick={() => setIsOpen(false)}
              >
                <Button
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2 rounded-lg font-mono uppercase tracking-widest text-xs"
                >
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}